/**
 * Payment step — mounts Square Web Payments SDK card element and tokenizes
 * the card into a cnon:... nonce, then attaches it to the cart.
 *
 * Shadow DOM caveat: Square's Card element renders inside its own iframe,
 * which insulates it from most shadow DOM issues. We mount inside our
 * shadow root like any other step.
 *
 * 3DS: we call payments.verifyBuyer() before tokenize() when the amount is
 * > 0, passing SCA-required details. Square handles the challenge UX.
 *
 * This is intentionally Card-only for MVP. Apple/Google Pay buttons add
 * non-trivial complexity (domain registration + separate init flow) and
 * are deferred to a follow-up.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import type { Cart, DepositInfo } from '../types';
import { LockIcon, BackIcon } from '../icons';
import { track } from '../firetrack';
import { PromoCode } from '../components/PromoCode';

// Stripe Elements types — narrow surface, declared inline to keep the
// @stripe/stripe-js import lazy. The actual `loadStripe` import happens
// inside the Stripe useEffect so the dep is tree-shaken from non-Stripe
// tenants' bundles.
interface StripeJsGlobal {
  elements(): StripeElementsInstance;
  // Card Element API (matches `elements.create('card')` above): pass the
  // Card Element reference directly with billing_details at the top level.
  // Distinct from the newer Payment Element API which takes `{ elements,
  // params }` — that variant needs a clientSecret on the Elements
  // instance, which we don't have at tokenize-time (server creates the
  // PaymentIntent AFTER receiving the pm_xxx id).
  createPaymentMethod(args: {
    type: 'card';
    card: StripeCardElementInstance;
    billing_details?: { name?: string; email?: string };
  }): Promise<{ paymentMethod?: { id: string }; error?: { message?: string } }>;
}
interface StripeCardElementOptions {
  style?: {
    base?: Record<string, unknown>;
    invalid?: Record<string, unknown>;
  };
  hidePostalCode?: boolean;
}
interface StripeElementsInstance {
  create(type: 'card', options?: StripeCardElementOptions): StripeCardElementInstance;
}
interface StripeCardElementInstance {
  mount(element: HTMLElement): void;
  destroy(): void;
  on(event: 'change', handler: (ev: { error?: { message: string } | null }) => void): void;
}

const SQUARE_SCRIPT_ID = 'sb-widget-square-web-payments';

/** Map an sb-vault SBV_ERROR code to customer-friendly copy. */
function mapVaultError(code?: string): string | null {
  switch (code) {
    case 'CARD_INVALID':
      return 'Please check the card number and expiry.';
    case 'RATE_LIMITED':
      return 'Too many attempts. Please wait a moment.';
    case 'NONCE_EXPIRED':
    case 'NONCE_INVALID':
      return 'This session expired. Please reopen the booking.';
    default:
      return null;
  }
}

/**
 * Ref-count of active Payment mounts using the Square Web Payments SDK.
 * The Square `<script>` tag we inject into `document.head` only stays in
 * the DOM while at least one Payment step is alive — when the count hits
 * zero (last close()), the cleanup removes our tag. Square's loaded SDK
 * stays in `window.Square` so a subsequent mount re-uses it without
 * re-injecting; the ref count is just the "who's still using the tag"
 * accounting that lets us avoid a leak across open/close cycles.
 */
let squareSdkMountCount = 0;

function formatMoney(cents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
  }).format(cents / 100);
}

async function ensureSquareLoaded(scriptUrl: string): Promise<SquareGlobal> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as unknown as { Square?: SquareGlobal };
  if (w.Square) return w.Square;

  const existing = document.getElementById(SQUARE_SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    await new Promise<void>((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('Square SDK script failed to load')),
        {
          once: true,
        },
      );
    });
    if (!w.Square) throw new Error('Square SDK loaded but window.Square missing');
    return w.Square;
  }

  const script = document.createElement('script');
  script.id = SQUARE_SCRIPT_ID;
  script.src = scriptUrl;
  script.async = true;
  // Marker attribute so the ref-counted cleanup only removes script tags
  // we ourselves injected — leaves alone anything a host page might have
  // dropped under the same id (unlikely, but cheap insurance).
  script.dataset.sbInjected = 'true';
  document.head.appendChild(script);

  await new Promise<void>((resolve, reject) => {
    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('Square SDK script failed to load')), {
      once: true,
    });
  });
  if (!w.Square) throw new Error('Square SDK loaded but window.Square missing');
  return w.Square;
}

// Minimal typing for the Web Payments SDK surface we use.
interface SquarePayments {
  card(): Promise<SquareCard>;
  verifyBuyer(sourceId: string, details: VerifyBuyerDetails): Promise<{ token: string }>;
}
interface SquareCard {
  attach(selector: Element | string): Promise<void>;
  tokenize(): Promise<TokenizeResult>;
  destroy(): Promise<void>;
}
interface TokenizeResult {
  status: 'OK' | 'Cancel' | string;
  token?: string;
  errors?: Array<{ message?: string; field?: string; type?: string }>;
}
interface VerifyBuyerDetails {
  amount: string; // e.g. "15.00"
  currencyCode: string;
  intent: 'CHARGE' | 'STORE';
  billingContact?: {
    givenName?: string;
    familyName?: string;
    email?: string;
  };
}
interface SquareGlobal {
  payments(applicationId: string, locationId: string): SquarePayments;
}

interface PaymentStepProps {
  /**
   * The held slot's start (offset-carrying ISO) + salon tz + stylist name,
   * echoed above the pay form so the customer sees exactly WHAT they're
   * securing (Odessa consult feedback: the payment screen never showed the
   * appointment time). Optional — omitted when no slot context exists.
   */
  apptStartAt?: string | null;
  timezone?: string;
  staffName?: string | null;
  deposit: DepositInfo;
  cart: Cart;
  /**
   * Promo-code hooks. Absent means the salon has no codes enabled, and
   * the entry point is not rendered at all — HighLevel-only for now.
   */
  onApplyPromo?: (code: string) => Promise<{ ok: boolean; message?: string }>;
  /** P4 — switch between paying the deposit and paying the whole price. */
  onSetPaymentMode?: (payInFull: boolean) => Promise<void>;
  /**
   * A payment-mode change left the displayed amount possibly out of step
   * with what the server holds. Blocks payment: charging an amount we
   * cannot vouch for is worse than making the customer reload.
   */
  quoteStale?: boolean;
  onRemovePromo?: () => Promise<void>;
  /** Known only right after an apply; null after a fresh cart load. */
  appliedPromoLabel?: string | null;
  client: {
    givenName?: string;
    familyName?: string;
    email?: string;
  };
  onAttached: (
    sourceId: string,
    verificationToken?: string,
    cardMeta?: {
      brand?: string;
      last4?: string;
      expMonth?: number;
      expYear?: number;
    },
    policyAccepted?: boolean,
    /** The save-card wording displayed when the tick was given (Stripe). */
    policyTextSeen?: string,
  ) => void;
  onBack: () => void;
  submitting: boolean;
  /**
   * The widget's OWN origin — where blvd-card-frame.html is served
   * (app.salonlift.net), derived from the widget.js script src. Distinct from
   * the page origin: when the widget is embedded on a salon's site, the card
   * frame is cross-origin to the page, so the src, the postMessage target, and
   * the origin check must all use THIS, not window.location.origin.
   */
  frameOrigin: string;
  /** Tenant slug — passed to the frame as ?m= so its route can scope the
   *  frame-ancestors CSP to this tenant's registered origins. */
  merchant: string;
  /** Salon display name — "Charged by {merchantName} to this card" (D71). */
  merchantName?: string | null;
  /**
   * Per-tenant view-bundle flag. 'be-you' suppresses the "Secure your
   * slot" eyebrow per the med-spa CRO direction. Null = default.
   */
  customViewBundle?: string | null;
}

export function PaymentStep({
  apptStartAt,
  timezone,
  staffName,
  deposit,
  cart,
  onApplyPromo,
  onSetPaymentMode,
  quoteStale,
  onRemovePromo,
  appliedPromoLabel,
  client,
  onAttached,
  onBack,
  submitting,
  customViewBundle,
  frameOrigin,
  merchant,
  merchantName,
}: PaymentStepProps) {
  const isBeYou = customViewBundle === 'be-you';
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const lightMountRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<SquareCard | null>(null);
  const paymentsRef = useRef<SquarePayments | null>(null);
  // Stripe Elements refs — populated only when tokenization.kind === 'stripe'.
  // Square's refs above and these are mutually exclusive at runtime; the
  // useEffect kind-guards ensure only one branch ever activates per mount.
  const stripeRef = useRef<StripeJsGlobal | null>(null);
  const stripeElementsRef = useRef<StripeElementsInstance | null>(null);
  const stripeCardRef = useRef<StripeCardElementInstance | null>(null);
  const [sdkState, setSdkState] = useState<
    | 'loading'
    | 'ready'
    | 'error'
    | 'mock'
    | 'blvd'
    | 'blvd-attached'
    | 'stripe'
    | 'stripe-attached'
    | 'sb-vault'
    | 'sb-vault-attached'
  >('loading');
  const [sdkError, setSdkError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [consentAccepted, setConsentAccepted] = useState(false);
  // Consent applies ONLY to the sb-vault card-on-file flow — existing provider
  // deposit flows are unchanged. Reuses the deposit policy text/flag for Phase 3
  // (a versioned no-show policy is Phase 4).
  const requiresConsent =
    deposit.tokenization.kind === 'sb-vault' &&
    (deposit.required === true || deposit.cardOnFile === true);
  /**
   * The Stripe salon's OPT-IN offer to keep the card for future visits.
   * Deliberately a separate tick from `requiresConsent`: that one is a
   * condition of booking (no card, no appointment), this one never is —
   * the client is paying a deposit either way, and keeping the card
   * afterwards is an extra they may decline. Held in its own state so the
   * two can never stand in for one another.
   *
   * Whether to offer at all is the server's answer (deposit.saveCardOffered).
   */
  const offersSaveCard = deposit.saveCardOffered === true && deposit.tokenization.kind === 'stripe';
  /** The wording the offer is made on — its own, not the deposit policy. */
  const saveCardPolicyText = deposit.saveCardPolicyText ?? null;
  const [saveCardAccepted, setSaveCardAccepted] = useState(false);
  // Read inside the Stripe submit handler, which closes over its first
  // render — the same reason consentAcceptedRef exists below.
  const saveCardAcceptedRef = useRef(saveCardAccepted);
  saveCardAcceptedRef.current = saveCardAccepted;
  // A tick belongs to the wording it was given for. This step stays
  // mounted while a re-quote replaces `deposit` — a discount applied here,
  // the pay-in-full toggle, an edited policy — so without this the tick
  // would carry over to wording the client never agreed to. Clearing it
  // makes them look again; the server refuses the mismatch anyway.
  useEffect(() => {
    setSaveCardAccepted(false);
  }, [offersSaveCard, saveCardPolicyText]);
  // "$0 card-on-file" mode — card required, nothing charged today. Drives the
  // no-deposit heading/sub + the "$0 due today" summary below.
  const isCardOnFile = deposit.cardOnFile === true;
  // D71 — card-on-file WITH a deposit the salon charges by hand. Same capture,
  // different story: "reserve with a card, {salon} charges {amount}".
  const cardOnFileDeposit = isCardOnFile && deposit.amountCents > 0;
  const salonLabel = merchantName?.trim() || 'the salon';
  // Lines whose stylist is exempt owe nothing (deposit.lines[].exempt). The
  // percent label is computed over the NON-exempt lines only, and shown only
  // when it is a whole number — "30%" under a "50% deposit" policy on a mixed
  // cart would read as a contradiction.
  const cofLines = deposit.lines ?? [];
  const exemptServiceIds = new Set(cofLines.filter((l) => l.exempt).map((l) => l.serviceId));
  // Server-computed (discounted) line prices, so the rows add up to the
  // subtotal even after a promo; falls back to the catalogue price for a
  // line the quote didn't cover.
  const cofPriceByService = new Map(cofLines.map((l) => [l.serviceId, l.priceCents]));
  const cofLinePrice = (l: { serviceId: string; price: { amountCents: number } }) =>
    cofPriceByService.get(l.serviceId) ?? l.price.amountCents;
  const nonExemptSubtotalCents = cart.lines
    .filter((l) => !exemptServiceIds.has(l.serviceId))
    .reduce((sum, l) => sum + cofLinePrice(l), 0);
  // The percent label is shown only when the POLICY is a percentage (a $50
  // flat deposit on a $100 service is not "a 50% policy" even though the
  // ratio says so), computed over the non-exempt lines, and only when whole.
  const cofIsPercentPolicy = cofLines.some((l) => !l.exempt && l.depositType === 'PERCENT');
  const cofPctRaw =
    cofIsPercentPolicy && nonExemptSubtotalCents > 0
      ? ((deposit.rawAmountCents ?? deposit.amountCents) / nonExemptSubtotalCents) * 100
      : null;
  const cofPct =
    cofPctRaw !== null && Math.abs(cofPctRaw - Math.round(cofPctRaw)) < 0.5
      ? Math.round(cofPctRaw)
      : null;
  // Refs so the sb-vault postMessage handler (a stable closure) reads current
  // values without re-subscribing the listener / remounting the iframe.
  const captureNonceRef = useRef(deposit.tokenization.captureNonce);
  captureNonceRef.current = deposit.tokenization.captureNonce;
  const consentAcceptedRef = useRef(consentAccepted);
  consentAcceptedRef.current = consentAccepted;

  // -------------------------------------------------------------------------
  // Boulevard branch: mount the same-origin PCI iframe, handshake the
  // tokenization endpoint via TOKENIZE_CARD_INIT, listen for TOKENIZE_SUCCESS
  // and auto-call onAttached. The iframe's own Pay button submits the form
  // (parent's actionbar is hidden when kind='blvd-vault'). Origin discipline
  // is enforced on every postMessage receive — only same-origin messages
  // are processed.
  // -------------------------------------------------------------------------
  useEffect(() => {
    const tok = deposit.tokenization;
    if (tok.kind !== 'blvd-vault' || !tok.endpoint) return;
    setSdkState('blvd');

    const placeholder = cardContainerRef.current;
    if (!placeholder) return;

    // The frame is served from OUR origin (frameOrigin), which is cross-origin
    // to the salon's page when the widget is embedded. An absolute src is
    // required — a root-relative '/blvd-card-frame.html' resolved against the
    // salon's domain and 404'd, which is the bug this fixes. ?m carries the
    // slug so the frame's route can scope its frame-ancestors CSP.
    const iframe = document.createElement('iframe');
    iframe.src = `${frameOrigin}/blvd-card-frame?m=${encodeURIComponent(merchant)}`;
    iframe.title = 'Card details';
    iframe.style.width = '100%';
    iframe.style.height = '400px';
    iframe.style.border = '0';
    iframe.style.borderRadius = '8px';
    iframe.setAttribute('aria-label', 'Credit or debit card');
    placeholder.appendChild(iframe);

    function onMessage(event: MessageEvent) {
      // Origin discipline: EXACT match on the frame's origin (not the page's),
      // AND bound to this iframe's window. Mirrors the sb-vault branch.
      if (event.origin !== frameOrigin) return;
      if (event.source !== iframe.contentWindow) return;
      const data = event.data as
        | { type?: string; token?: string; reason?: string; endpoint?: string }
        | null
        | undefined;
      if (!data || typeof data !== 'object') return;
      if (data.type === 'IFRAME_READY') {
        // Iframe finished loading — send INIT with the endpoint, targeted at
        // the frame's origin (not the page's).
        iframe.contentWindow?.postMessage(
          { type: 'TOKENIZE_CARD_INIT', endpoint: tok.endpoint },
          frameOrigin,
        );
        return;
      }
      if (data.type === 'TOKENIZE_SUCCESS' && typeof data.token === 'string') {
        setSdkState('blvd-attached');
        onAttached(data.token);
        return;
      }
      if (data.type === 'TOKENIZE_ERROR') {
        setFormError(data.reason ?? 'Payment could not be processed');
        return;
      }
    }
    // M22 — per-widget message listener. The origin check at the top of
    // onMessage already rejects everything that isn't same-origin, so
    // the cost is bounded even when the host page mounts multiple
    // widgets. Documented decision: a shared listener would have to
    // route by iframe window reference, which we don't currently track,
    // and the multi-widget-per-host case is rare. Revisit if/when host
    // pages routinely embed >1 widget.
    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
      iframe.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.tokenization.endpoint, frameOrigin, merchant]);

  // -------------------------------------------------------------------------
  // sb-vault branch: mount the CROSS-ORIGIN vault capture iframe and run the
  // SBV_* postMessage handshake. The PAN is typed in the vault iframe and never
  // reaches this app — we only receive {token, brand, last4, exp}. Every message
  // is rejected unless it is from the EXACT vault origin AND from this iframe's
  // own window. The captureNonce (server-minted, opaque) is read via ref so a
  // nonce refresh can't remount the iframe mid-handshake.
  // -------------------------------------------------------------------------
  useEffect(() => {
    const tok = deposit.tokenization;
    const VAULT = tok.vaultOrigin;
    if (tok.kind !== 'sb-vault' || !VAULT) return;
    setSdkState('sb-vault');

    const placeholder = cardContainerRef.current;
    if (!placeholder) return;

    const iframe = document.createElement('iframe');
    iframe.src = `${VAULT}/capture`;
    iframe.title = 'Card details';
    iframe.style.width = '100%';
    // Tall enough for the vault capture form's fields (card #, exp, required
    // name, required billing ZIP, save) without an inner scrollbar — matches
    // the Boulevard iframe above.
    iframe.style.height = '400px';
    iframe.style.border = '0';
    iframe.style.borderRadius = '8px';
    // The vault page fetches its OWN same-origin /v1/tokenize, so the iframe
    // must keep its (vault) origin — hence allow-same-origin. It remains
    // cross-origin to the salon page either way.
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms');
    iframe.setAttribute('allow', 'payment');
    iframe.setAttribute('aria-label', 'Credit or debit card');
    placeholder.appendChild(iframe);

    function onMessage(event: MessageEvent) {
      // Origin allow-list: EXACT equality with the vault origin (never '*',
      // never startsWith) AND bound to THIS iframe's window.
      if (!VAULT || event.origin !== VAULT) return;
      if (event.source !== iframe.contentWindow) return;
      const data = event.data as
        | {
            type?: string;
            token?: string;
            brand?: string;
            last4?: string;
            expMonth?: number;
            expYear?: number;
            code?: string;
          }
        | null
        | undefined;
      if (!data || typeof data !== 'object') return;

      if (data.type === 'SBV_READY') {
        const nonce = captureNonceRef.current;
        if (nonce) {
          iframe.contentWindow?.postMessage({ type: 'SBV_INIT', captureNonce: nonce }, VAULT);
        }
        return;
      }
      if (data.type === 'SBV_SUCCESS' && typeof data.token === 'string') {
        setSdkState('sb-vault-attached');
        onAttached(
          data.token,
          undefined,
          {
            brand: data.brand,
            last4: data.last4,
            expMonth: data.expMonth,
            expYear: data.expYear,
          },
          consentAcceptedRef.current,
        );
        return;
      }
      if (data.type === 'SBV_ERROR') {
        setFormError(mapVaultError(data.code) ?? 'Payment could not be processed');
        return;
      }
    }
    window.addEventListener('message', onMessage);

    return () => {
      window.removeEventListener('message', onMessage);
      iframe.remove();
    };
    // captureNonce intentionally omitted — read via ref (see above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.tokenization.kind, deposit.tokenization.vaultOrigin]);

  useEffect(() => {
    let cancelled = false;

    const tok = deposit.tokenization;
    // Boulevard branch handled in the dedicated effect above.
    if (tok.kind === 'blvd-vault') return;
    // sb-vault branch handled in its dedicated effect above — guard BEFORE the
    // mock fallthrough so an sb-vault tenant never renders the static card mock.
    if (tok.kind === 'sb-vault') return;
    // Stripe branch (HighLevel tenants) handled in the dedicated effect
    // below. Without this guard, the kind-not-recognized fall-through
    // would render the static mock for HighLevel deposits.
    if (tok.kind === 'stripe') return;
    if (tok.kind !== 'square-web-payments' || !tok.publicKey || !tok.locationId || !tok.scriptUrl) {
      // Render the design's static card mock so operators can preview
      // the deposit screen before Square Web Payments is provisioned
      // (or while testMode is on without test creds). The Pay button
      // stays disabled so nothing fires.
      setSdkState('mock');
      return;
    }

    // Square Web Payments SDK validates the mount target via
    // `document.contains(element)`, which returns false for elements
    // inside a shadow root. Our widget runs entirely shadow-DOMed for
    // style isolation, so attaching to `cardContainerRef.current`
    // throws "The element DIV was not found".
    //
    // Claim a Square-SDK reference. Cleanup decrements and removes the
    // script tag when this is the last mount.
    squareSdkMountCount++;

    // Workaround: create a sibling div in the LIGHT DOM (appended to
    // <body>) with `position: fixed`, pass that to card.attach, and
    // mirror the in-shadow placeholder's viewport-relative rect onto
    // it. `position: fixed` (not absolute) is what makes this robust
    // when the widget is embedded inside a scrollable parent (modal,
    // drawer, sticky panel) — getBoundingClientRect returns viewport
    // coords regardless of which container is scrolling, so a fixed-
    // positioned overlay tracks the placeholder cleanly. The capture-
    // phase scroll listener on window catches inner-container scrolls
    // since they propagate through the capture path even though they
    // don't bubble.
    const lightMount = document.createElement('div');
    lightMount.setAttribute('data-sb-card-portal', '');
    lightMount.style.position = 'fixed';
    lightMount.style.zIndex = '2147483646';
    document.body.appendChild(lightMount);
    lightMountRef.current = lightMount;

    function syncPosition() {
      const placeholder = cardContainerRef.current;
      if (!placeholder || cancelled) return;
      const rect = placeholder.getBoundingClientRect();
      lightMount.style.top = rect.top + 'px';
      lightMount.style.left = rect.left + 'px';
      lightMount.style.width = rect.width + 'px';
      lightMount.style.height = rect.height + 'px';
    }

    syncPosition();
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && cardContainerRef.current) {
      ro = new ResizeObserver(syncPosition);
      ro.observe(cardContainerRef.current);
    }
    // Capture phase + true so we catch scrolls inside .sbw-scroll too.
    // M21 — passive: true so the browser knows we'll never preventDefault
    // and can keep scrolling on the compositor thread. syncPosition only
    // reads getBoundingClientRect and writes to inline styles, so it's
    // safe to mark passive. Pair with capture: true (was the trailing
    // boolean arg) so inner-container scrolls still hit us.
    window.addEventListener('scroll', syncPosition, { passive: true, capture: true });
    window.addEventListener('resize', syncPosition, { passive: true });
    // Android: the URL-bar collapse and the soft keyboard (which opens
    // the moment the customer taps the card field) change the VISUAL
    // viewport without firing a window resize — Chrome 108+ keeps the
    // layout viewport stable under the keyboard, and Samsung Internet
    // is patchier still. Without these, the fixed portal detaches from
    // the placeholder exactly when the customer starts typing
    // (2026-06-10 Good Vibes Android report: blank card box →
    // tokenize() on an empty card → "Credit card number is not valid").
    const vv = window.visualViewport;
    vv?.addEventListener('resize', syncPosition, { passive: true });
    vv?.addEventListener('scroll', syncPosition, { passive: true });
    // Belt-and-suspenders for browsers that drop some of the events
    // above: keep the portal pinned for the life of the Payment step.
    // Cheap — syncPosition is one getBoundingClientRect + 4 style writes.
    const pinInterval = window.setInterval(syncPosition, 250);

    (async () => {
      try {
        const Square = await ensureSquareLoaded(tok.scriptUrl!);
        if (cancelled) return;
        const payments = Square.payments(tok.publicKey!, tok.locationId!);
        paymentsRef.current = payments;
        const card = await payments.card();
        if (cancelled) {
          await card.destroy().catch(() => void 0);
          return;
        }
        await card.attach(lightMount);
        cardRef.current = card;
        // Re-sync after attach because the iframe sets its own height.
        syncPosition();
        setSdkState('ready');
      } catch (e) {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : String(e);
        setSdkError(msg);
        setSdkState('error');
        track('payment_sdk_error', { provider: 'square', message: msg });
      }
    })();

    return () => {
      cancelled = true;
      ro?.disconnect();
      // removeEventListener matches on (type, listener, capture); the
      // passive flag isn't part of the listener identity, so the same
      // capture: true used at addEventListener time suffices here.
      window.removeEventListener('scroll', syncPosition, { capture: true });
      window.removeEventListener('resize', syncPosition);
      vv?.removeEventListener('resize', syncPosition);
      vv?.removeEventListener('scroll', syncPosition);
      window.clearInterval(pinInterval);
      cardRef.current?.destroy().catch(() => void 0);
      cardRef.current = null;
      lightMount.remove();
      lightMountRef.current = null;
      // Release our Square-SDK ref. When the last Payment mount unwinds,
      // remove the script tag we injected. The Square SDK already lives
      // in window.Square; future mounts re-use it via the early return
      // in ensureSquareLoaded() — re-injection is not required.
      squareSdkMountCount--;
      if (squareSdkMountCount === 0) {
        const ours = document.getElementById(SQUARE_SCRIPT_ID);
        if (ours && (ours as HTMLScriptElement).dataset.sbInjected === 'true') {
          ours.remove();
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deposit.tokenization.publicKey, deposit.tokenization.locationId]);

  // ---------------------------------------------------------------------------
  // Stripe branch (HighLevel tenants per server H9 + H21). Loads
  // @stripe/stripe-js (~5KB wrapper; actual Stripe.js SDK lazy-loads from
  // js.stripe.com at runtime), mounts Stripe Elements card field.
  //
  // Stripe Elements REFUSES to mount inside a Shadow Root ("Elements
  // cannot be mounted in a ShadowRoot. Please mount in the Light DOM.").
  // Our widget runs entirely shadow-DOMed for style isolation, so we use
  // the same light-DOM portal trick as Square: create a fixed-positioned
  // <div> in document.body, mirror the shadow-DOM placeholder's viewport
  // rect onto it, mount cardElement there.
  //
  // 3DS / SCA handling is NOT wired here — server's checkout orchestrator
  // (Phase 3b.2) returns `requires_action` with clientSecret in error.cause
  // when SCA is required. Widget-side handleNextAction(clientSecret) is a
  // Phase 3b.4 follow-up; test-mode + US-only med spa won't hit SCA.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const tok = deposit.tokenization;
    if (tok.kind !== 'stripe' || !tok.publicKey) return;

    const placeholder = cardContainerRef.current;
    if (!placeholder) return;

    setSdkState('loading');
    setSdkError(null);

    // Light-DOM portal (same recipe as the Square branch above).
    // Styled like a real input — Stripe Card Element renders only the
    // placeholders/iframe contents; the surrounding "input" appearance
    // (bg, border, padding) is ours to draw on the mount container.
    const lightMount = document.createElement('div');
    lightMount.setAttribute('data-sb-stripe-portal', '');
    lightMount.style.position = 'fixed';
    lightMount.style.zIndex = '2147483646';
    lightMount.style.background = '#ffffff';
    lightMount.style.border = '1px solid #e5e5e5';
    lightMount.style.borderRadius = '8px';
    lightMount.style.padding = '12px 14px';
    lightMount.style.boxSizing = 'border-box';
    document.body.appendChild(lightMount);
    lightMountRef.current = lightMount;

    function syncPosition() {
      const ph = cardContainerRef.current;
      if (!ph || cancelled) return;
      const rect = ph.getBoundingClientRect();
      lightMount.style.top = rect.top + 'px';
      lightMount.style.left = rect.left + 'px';
      lightMount.style.width = rect.width + 'px';
      lightMount.style.height = rect.height + 'px';
    }

    syncPosition();
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && cardContainerRef.current) {
      ro = new ResizeObserver(syncPosition);
      ro.observe(cardContainerRef.current);
    }
    window.addEventListener('scroll', syncPosition, { passive: true, capture: true });
    window.addEventListener('resize', syncPosition, { passive: true });
    // Android visual-viewport hardening — see the Square branch above
    // for the full rationale (URL bar + soft keyboard move the visual
    // viewport without a window resize).
    const vv = window.visualViewport;
    vv?.addEventListener('resize', syncPosition, { passive: true });
    vv?.addEventListener('scroll', syncPosition, { passive: true });
    const pinInterval = window.setInterval(syncPosition, 250);

    (async () => {
      try {
        const { loadStripe } = await import('@stripe/stripe-js');
        if (cancelled) return;
        const stripe = (await loadStripe(tok.publicKey!)) as unknown as StripeJsGlobal | null;
        if (cancelled) return;
        if (!stripe) {
          setSdkState('error');
          setSdkError('Could not initialise Stripe. Try refreshing.');
          return;
        }
        stripeRef.current = stripe;
        const elements = stripe.elements();
        stripeElementsRef.current = elements;
        // Style config affects the IFRAME contents (cross-origin sandbox);
        // outer container styling lives on `lightMount` above.
        const cardElement = elements.create('card', {
          style: {
            base: {
              fontSize: '15px',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
              color: '#0a0a0a',
              '::placeholder': { color: '#a3a3a3' },
            },
            invalid: { color: '#dc2626' },
          },
          hidePostalCode: false,
        });
        stripeCardRef.current = cardElement;

        // Surface card-validation errors inline (matches Square's
        // formError UX). Stripe fires `change` events whenever the
        // card field's validity state changes.
        cardElement.on('change', (ev) => {
          if (cancelled) return;
          setFormError(ev.error?.message ?? null);
        });

        cardElement.mount(lightMount);
        if (cancelled) {
          cardElement.destroy();
          return;
        }
        // Re-sync after mount because the iframe sets its own height.
        syncPosition();
        setSdkState('stripe');
      } catch (err) {
        if (cancelled) return;
        // eslint-disable-next-line no-console
        console.error('[sb-widget] Stripe init failed:', err);
        setSdkState('error');
        setSdkError(err instanceof Error ? err.message : 'Stripe failed to load');
      }
    })();

    return () => {
      cancelled = true;
      ro?.disconnect();
      window.removeEventListener('scroll', syncPosition, { capture: true });
      window.removeEventListener('resize', syncPosition);
      vv?.removeEventListener('resize', syncPosition);
      vv?.removeEventListener('scroll', syncPosition);
      window.clearInterval(pinInterval);
      try {
        stripeCardRef.current?.destroy();
      } catch {
        // Best-effort — Stripe sometimes complains if the element was
        // never mounted (race between cleanup + async init).
      }
      stripeCardRef.current = null;
      stripeElementsRef.current = null;
      stripeRef.current = null;
      lightMount.remove();
      lightMountRef.current = null;
    };
  }, [deposit.tokenization.kind, deposit.tokenization.publicKey]);

  async function handlePay(e: Event) {
    e.preventDefault();
    setFormError(null);

    // Stripe branch (HighLevel) — tokenize via createPaymentMethod and
    // hand the pm_xxx id back to the cart attach call. 3DS / SCA is
    // not handled here; see Phase 3b.4 TODO note above the Stripe useEffect.
    if (deposit.tokenization.kind === 'stripe') {
      return handleStripePay();
    }

    const card = cardRef.current;
    const payments = paymentsRef.current;
    if (!card || !payments) {
      setFormError('Payment form not ready. Refresh and try again.');
      return;
    }

    setProcessing(true);
    try {
      const result = await card.tokenize();
      if (result.status !== 'OK' || !result.token) {
        const msg = result.errors?.[0]?.message ?? 'Unable to process card. Please try again.';
        setFormError(msg);
        // Diagnostics for the Android blank-card-box class of failure
        // (2026-06-10 Good Vibes): a tokenize failure with a zero-sized
        // or off-viewport portal means the customer never SAW the card
        // fields — a positioning bug, not a typo. Lands in FireTrack so
        // device-specific reports are debuggable without a repro device.
        const portal = lightMountRef.current?.getBoundingClientRect();
        const ph = cardContainerRef.current?.getBoundingClientRect();
        track('payment_tokenize_failed', {
          provider: 'square',
          message: msg,
          portal_rect: portal
            ? {
                t: Math.round(portal.top),
                l: Math.round(portal.left),
                w: Math.round(portal.width),
                h: Math.round(portal.height),
              }
            : null,
          placeholder_rect: ph
            ? {
                t: Math.round(ph.top),
                l: Math.round(ph.left),
                w: Math.round(ph.width),
                h: Math.round(ph.height),
              }
            : null,
          viewport: { w: window.innerWidth, h: window.innerHeight },
          visual_viewport: window.visualViewport
            ? {
                w: Math.round(window.visualViewport.width),
                h: Math.round(window.visualViewport.height),
              }
            : null,
        });
        return;
      }

      // 3DS verifyBuyer when required (SCA-likely card, amount > 0).
      let verificationToken: string | undefined;
      try {
        const verif = await payments.verifyBuyer(result.token, {
          amount: (deposit.amountCents / 100).toFixed(2),
          currencyCode: deposit.currency,
          intent: 'CHARGE',
          billingContact: {
            givenName: client.givenName,
            familyName: client.familyName,
            email: client.email,
          },
        });
        verificationToken = verif?.token;
      } catch (verifErr) {
        // 3DS verification is advisory — if the card doesn't require it,
        // Square may throw. Proceed without verificationToken; CreatePayment
        // will reject server-side if SCA is actually required.
        // eslint-disable-next-line no-console
        console.warn('[sb-widget] verifyBuyer non-fatal error:', verifErr);
      }

      onAttached(result.token, verificationToken);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    } finally {
      setProcessing(false);
    }
  }

  async function handleStripePay() {
    const stripe = stripeRef.current;
    const cardElement = stripeCardRef.current;
    if (!stripe || !cardElement) {
      setFormError('Payment form not ready. Refresh and try again.');
      return;
    }

    setProcessing(true);
    try {
      const billingName = [client.givenName, client.familyName].filter(Boolean).join(' ').trim();
      const result = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          ...(billingName ? { name: billingName } : {}),
          ...(client.email ? { email: client.email } : {}),
        },
      });
      if (result.error || !result.paymentMethod) {
        setFormError(result.error?.message ?? 'Unable to process card. Please try again.');
        return;
      }
      // Pass the Stripe payment_method.id (pm_xxx) back to the server.
      // 3DS/SCA, if required, surfaces from the server's checkout
      // response — Phase 3b.4 wires the widget-side handleNextAction.
      //
      // The save-card tick rides along on EVERY attach, false included: the
      // server records consent per attach, so a client who ticks, changes
      // their mind and re-enters a different card must not have the second
      // card kept on the first card's tick. Sent only where the offer was
      // actually made, so an untouched box on a salon that never offered
      // cannot read as a decline.
      setSdkState('stripe-attached');
      onAttached(
        result.paymentMethod.id,
        undefined,
        undefined,
        offersSaveCard ? saveCardAcceptedRef.current : undefined,
        // What the tick was given for. The server records its OWN wording
        // and uses this only to check the two still agree, so an edit made
        // while this client sat on the payment step refuses rather than
        // records something they never read.
        offersSaveCard && saveCardAcceptedRef.current
          ? (saveCardPolicyText ?? undefined)
          : undefined,
      );
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    } finally {
      setProcessing(false);
    }
  }

  // The EFFECTIVE subtotal — catalogue price less any promo code (D69).
  // The server's deposit is computed on the same basis, so summing raw
  // prices here would show a customer a total that disagrees with what
  // their card is about to be charged against.
  const subtotalCents = cart.lines.reduce(
    (sum, l) => sum + Math.max(0, l.price.amountCents - (l.discountCents ?? 0)),
    0,
  );
  const discountCents = cart.lines.reduce((sum, l) => sum + (l.discountCents ?? 0), 0);
  // No `appliedPromoCode` on the cart, deliberately: the discount amount
  // is what the customer needs to see, and inventing a server field just
  // to echo the string back would be a schema change for a label. The
  // component names the code when the parent happens to know it (right
  // after an apply) and says "Promo code applied" otherwise.
  // Sales tax (only a salon with a rate — Be You — gets these fields; for
  // everyone else both are 0 and every figure below is unchanged). The
  // charge can include tax; the subtotal never does, so comparing the two
  // raw read pay-in-full as a "108%" deposit.
  const taxCents = deposit.taxCents ?? 0;
  const fullTaxCents = deposit.fullTaxCents ?? 0;
  const preTaxChargeCents = deposit.amountCents - taxCents;
  const balanceCents = Math.max(0, subtotalCents + fullTaxCents - deposit.amountCents);
  const depositPct =
    subtotalCents > 0 ? Math.round((preTaxChargeCents / subtotalCents) * 1000) / 10 : null;
  // Be You-only: when deposit covers the full subtotal, the customer is
  // paying for a multi-session package, subscription, or sub-$50 service
  // in one shot — the "hold with a deposit" framing is misleading and
  // the $0 balance row reads as confusing dead weight. Switch the copy
  // to "Pay for your package" + drop the balance row. Gated to BeYou
  // because changing this copy for every tenant risks landing wording
  // that doesn't match what other operators promised their customers
  // (H22 — variant-scoped UI changes only).
  const payingInFull = isBeYou && preTaxChargeCents === subtotalCents && subtotalCents > 0;
  const sessionsMatch =
    payingInFull && cart.lines[0] ? cart.lines[0].serviceName.match(/(\d+)\s+sessions?\b/i) : null;
  const sessionCount = sessionsMatch ? parseInt(sessionsMatch[1]!, 10) : null;
  const perSessionCents =
    sessionCount && sessionCount > 1 && subtotalCents > 0
      ? Math.round(subtotalCents / sessionCount)
      : null;

  return (
    <form class="sbw-scroll sbw-payment" onSubmit={handlePay}>
      {!isBeYou && <div class="sbw-eyebrow">Secure your slot</div>}
      <h2 class="sbw-step-title">
        {isCardOnFile
          ? cardOnFileDeposit
            ? 'Reserve with a card.'
            : 'Reserve your appointment.'
          : payingInFull
            ? sessionCount && sessionCount > 1
              ? 'Pay for your package.'
              : 'Pay in full.'
            : 'Hold with a deposit.'}
      </h2>
      <p class="sbw-step-sub">
        {isCardOnFile
          ? cardOnFileDeposit
            ? // Short and specific. The full policy is the consent checkbox
              // below — repeating it here read as a stutter (Matt, 2026-09-16).
              `Save a card to reserve. ${salonLabel} will charge a ${formatMoney(deposit.amountCents, deposit.currency)}${cofPct !== null ? ` (${cofPct}%)` : ''} deposit to it once your booking is confirmed — the rest is paid at your appointment.`
            : 'A card holds your appointment — $0 today. You’re only charged for a late cancellation or no-show, per the policy below.'
          : payingInFull
            ? sessionCount && sessionCount > 1
              ? `${formatMoney(deposit.amountCents, deposit.currency)} prepays all ${sessionCount} sessions at package pricing — no balance due at your appointments.`
              : `${formatMoney(deposit.amountCents, deposit.currency)} paid in full today — no balance due at your appointment.`
            : (deposit.policyText ??
              `${formatMoney(deposit.amountCents, deposit.currency)} holds your spot. Balance is paid at the appointment.`)}
      </p>

      {apptStartAt && timezone && (
        <div class="sbw-payment-when">
          {new Date(apptStartAt).toLocaleString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            timeZone: timezone,
          })}
          {staffName ? ` · with ${staffName}` : ''}
        </div>
      )}

      {cardOnFileDeposit ? (
        <div class="sbw-deposit-card">
          {cart.lines.map((line) => {
            const exempt = exemptServiceIds.has(line.serviceId);
            return (
              <div key={line.lineId} class="sbw-deposit-row">
                <span>
                  {line.serviceName}
                  {exempt && (
                    <span
                      style={{
                        display: 'block',
                        fontSize: 11,
                        color: 'var(--sbw-ink-3)',
                        marginTop: 2,
                      }}
                    >
                      no deposit
                    </span>
                  )}
                </span>
                <span class="sbw-amt">{formatMoney(cofLinePrice(line), line.price.currency)}</span>
              </div>
            );
          })}
          <div class="sbw-deposit-row" style={{ color: 'var(--sbw-ink-3)' }}>
            <span>Subtotal</span>
            <span class="sbw-amt">
              {formatMoney(
                cart.lines.reduce((sum, l) => sum + cofLinePrice(l), 0),
                deposit.currency,
              )}
            </span>
          </div>
          <div class="sbw-deposit-row is-emph">
            <span>
              Deposit
              {cofPct !== null && (
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--sbw-ink-3)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    marginLeft: 6,
                  }}
                >
                  {cofPct}%
                </span>
              )}
              <span
                style={{
                  display: 'block',
                  fontSize: 11,
                  color: 'var(--sbw-ink-3)',
                  marginTop: 2,
                  fontWeight: 400,
                  textTransform: 'none',
                  letterSpacing: 0,
                }}
              >
                Charged by {salonLabel} to this card
              </span>
            </span>
            <span class="sbw-amt">{formatMoney(deposit.amountCents, deposit.currency)}</span>
          </div>
          <div class="sbw-deposit-row" style={{ color: 'var(--sbw-ink-3)', fontSize: 12 }}>
            <span>Balance at appointment</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatMoney(
                Math.max(
                  0,
                  cart.lines.reduce((sum, l) => sum + cofLinePrice(l), 0) - deposit.amountCents,
                ),
                deposit.currency,
              )}
            </span>
          </div>
        </div>
      ) : isCardOnFile ? (
        <div class="sbw-deposit-card">
          {cart.lines.map((line) => (
            <div key={line.lineId} class="sbw-deposit-row">
              <span>{line.serviceName}</span>
              <span class="sbw-amt">
                {formatMoney(line.price.amountCents, line.price.currency)}
              </span>
            </div>
          ))}
          <div class="sbw-deposit-row is-emph">
            <span>Due today</span>
            <span class="sbw-amt">{formatMoney(0, deposit.currency)}</span>
          </div>
          <div class="sbw-deposit-row" style={{ color: 'var(--sbw-ink-3)', fontSize: 12 }}>
            <span>Paid at your appointment</span>
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatMoney(subtotalCents, deposit.currency)}
            </span>
          </div>
        </div>
      ) : (
        <div class="sbw-deposit-card">
          {cart.lines.map((line) => (
            <div key={line.lineId} class="sbw-deposit-row">
              <span>
                {line.serviceName}
                {perSessionCents !== null && line === cart.lines[0] && (
                  <span
                    style={{
                      display: 'block',
                      fontSize: 11,
                      color: 'var(--sbw-ink-3)',
                      marginTop: 2,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {sessionCount} sessions · {formatMoney(perSessionCents, deposit.currency)} per
                    session
                  </span>
                )}
              </span>
              <span class="sbw-amt">
                {formatMoney(line.price.amountCents, line.price.currency)}
              </span>
            </div>
          ))}
          {/* P4 — deposit vs pay in full. Only offered when the tenant has
            turned it on AND the two figures actually differ; a "choice"
            between $50 and $50 is noise. Both numbers come from the
            server, so this control never does arithmetic on prices. */}
          {onSetPaymentMode &&
            deposit.allowPayInFull &&
            typeof deposit.fullAmountCents === 'number' &&
            typeof deposit.depositAmountCents === 'number' &&
            // Compare the two STABLE figures. Comparing against
            // `amountCents` meant that choosing "pay in full" made it equal
            // `fullAmountCents`, the condition went false, and the control
            // removed itself — stranding the customer on the choice they
            // had just made with no way back to the deposit.
            deposit.fullAmountCents > deposit.depositAmountCents && (
              <div class="sbw-paymode" role="group" aria-label="How much to pay now">
                <button
                  type="button"
                  class={`sbw-paymode__opt${!deposit.payInFull ? ' is-selected' : ''}`}
                  aria-pressed={!deposit.payInFull}
                  disabled={processing || submitting}
                  onClick={() => {
                    void onSetPaymentMode(false);
                  }}
                >
                  <span class="sbw-paymode__amt">
                    {formatMoney(deposit.depositAmountCents, deposit.currency)}
                  </span>
                  <span class="sbw-paymode__lbl">Deposit now</span>
                </button>
                <button
                  type="button"
                  class={`sbw-paymode__opt${deposit.payInFull ? ' is-selected' : ''}`}
                  aria-pressed={deposit.payInFull === true}
                  disabled={processing || submitting}
                  onClick={() => {
                    void onSetPaymentMode(true);
                  }}
                >
                  <span class="sbw-paymode__amt">
                    {formatMoney(deposit.fullAmountCents, deposit.currency)}
                  </span>
                  <span class="sbw-paymode__lbl">Pay in full</span>
                </button>
              </div>
            )}

          {onApplyPromo && onRemovePromo && (
            <PromoCode
              appliedCode={appliedPromoLabel ?? null}
              discountCents={discountCents}
              currency={deposit.currency}
              onApply={onApplyPromo}
              onRemove={onRemovePromo}
              disabled={submitting}
              formatMoney={formatMoney}
            />
          )}
          <div class="sbw-deposit-row" style={{ color: 'var(--sbw-ink-3)' }}>
            <span>Subtotal</span>
            <span class="sbw-amt">{formatMoney(subtotalCents, deposit.currency)}</span>
          </div>
          {taxCents > 0 && (
            <div class="sbw-deposit-row" style={{ color: 'var(--sbw-ink-3)' }}>
              <span>Sales tax</span>
              <span class="sbw-amt">{formatMoney(taxCents, deposit.currency)}</span>
            </div>
          )}
          <div class="sbw-deposit-row is-emph">
            <span>
              {payingInFull ? 'Pay today' : 'Deposit today'}
              {depositPct !== null && !payingInFull && (
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--sbw-ink-3)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.12em',
                    marginLeft: 6,
                  }}
                >
                  {depositPct}%
                </span>
              )}
            </span>
            <span class="sbw-amt">{formatMoney(deposit.amountCents, deposit.currency)}</span>
          </div>
          {!payingInFull && (
            <div class="sbw-deposit-row" style={{ color: 'var(--sbw-ink-3)', fontSize: 12 }}>
              <span>Balance at appointment{fullTaxCents > 0 && taxCents === 0 ? ' (incl. tax)' : ''}</span>
              <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                {formatMoney(balanceCents, deposit.currency)}
              </span>
            </div>
          )}
        </div>
      )}

      <div class="sbw-card-frame">
        <div class="sbw-card-label">
          <span>Card details</span>
          <span class="sbw-secured">
            <LockIcon />{' '}
            {deposit.tokenization.kind === 'stripe'
              ? 'Secured by Stripe'
              : deposit.tokenization.kind === 'blvd-vault'
                ? 'Secured by Boulevard'
                : deposit.tokenization.kind === 'sb-vault'
                  ? 'Secured by Salon Boss'
                  : 'Secured by Square'}
          </span>
        </div>
        {requiresConsent && (
          <label
            class="sbw-consent"
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
              fontSize: 13,
              margin: '4px 0 12px',
              color: 'var(--sbw-ink-2)',
            }}
          >
            <input
              type="checkbox"
              checked={consentAccepted}
              onChange={(e) => setConsentAccepted((e.target as HTMLInputElement).checked)}
              aria-label="Accept the cancellation and no-show policy"
            />
            <span>
              {deposit.policyText ??
                'I authorize keeping a card on file and agree to the cancellation / no-show policy.'}
            </span>
          </label>
        )}
        {sdkState === 'loading' && <div class="sbw-loading">Loading payment form…</div>}
        {sdkState === 'error' && (
          <div class="sbw-error" role="alert">
            {sdkError}
          </div>
        )}
        {sdkState === 'mock' && (
          <>
            <div class="sbw-card-mock">1234 1234 1234 1234</div>
            <div class="sbw-card-mock-row">
              <span style={{ flex: 1 }}>MM / YY</span>
              <span style={{ flex: 1 }}>CVC</span>
              <span style={{ flex: 1 }}>ZIP</span>
            </div>
            <div
              style={{
                marginTop: 8,
                fontSize: 11,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: 'var(--sbw-ink-3)',
              }}
            >
              Preview · payment not configured
            </div>
          </>
        )}
        <div
          ref={cardContainerRef}
          class="sbw-card-container"
          aria-label="Credit or debit card"
          // Reserve vertical space so the light-DOM portal has a
          // sized rect to overlay. Square's Card element renders
          // its own padded card-number row + MM/YY/CVV row + a
          // light internal border — measured at ~140px on a real
          // production load. Stripe Card Element is a single horizontal
          // row (number → MM/YY → CVC → ZIP appear inline as you type)
          // — ~48px is enough. Tokenization-kind-conditional so each
          // provider's element gets its natural sizing.
          style={
            sdkState === 'mock'
              ? { display: 'none' }
              : deposit.tokenization.kind === 'stripe'
                ? { minHeight: '48px', position: 'relative' }
                : deposit.tokenization.kind === 'sb-vault'
                  ? {
                      minHeight: '400px',
                      position: 'relative',
                      // Gate interaction until the no-show policy is accepted.
                      ...(requiresConsent && !consentAccepted
                        ? { opacity: 0.5, pointerEvents: 'none' as const }
                        : {}),
                    }
                  : { minHeight: '140px', position: 'relative' }
          }
        />
        {/* Below the card field, deliberately: the ask is about what
            happens to the card AFTER today, so it reads as a follow-up to
            entering one rather than a hurdle in front of it.

            The label is the whole ask. The salon's own retention wording
            sits under it as fine print — quieter, but present, because it
            is the text recorded as what this client agreed to and consent
            to wording nobody rendered is not consent (D73). An earlier
            version led with a sentence restating the policy in different
            words, which made the block read twice and pushed the card
            field down the screen.

            Never pre-ticked: consent to keep a payment card has to be
            given, not defaulted. */}
        {offersSaveCard && (
          <div class="sbw-save-card" style={{ margin: '12px 0 4px' }}>
            <label
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'center',
                fontSize: 14,
                // --sbw-ink, not --sbw-ink-1: there is no ink-1 token, and
                // an undefined var silently inherits whatever colour the
                // parent happens to carry.
                color: 'var(--sbw-ink)',
                cursor: 'pointer',
              }}
            >
              <input
                type="checkbox"
                checked={saveCardAccepted}
                onChange={(e) => setSaveCardAccepted((e.target as HTMLInputElement).checked)}
              />
              <span>Save my card for future visits</span>
            </label>
            <p
              style={{
                margin: '4px 0 0 24px',
                fontSize: 12,
                lineHeight: 1.5,
                // ink-2, not ink-3. Fine print still has to be READ — this
                // is the wording the client is agreeing to — and ink-3
                // (#8a8077 on the default cream) is the washed-out grey
                // this block was reported as unreadable in.
                color: 'var(--sbw-ink-2)',
              }}
            >
              {saveCardPolicyText}
            </p>
          </div>
        )}
        {/* The Boulevard and sb-vault iframes carry their own Pay/Save
            button, so the parent's actionbar is hidden for them (:1151).
            That left these two paths with NO feedback at all: the card
            tokenizes inside the iframe, the parent sets *-attached and
            silently starts the provider calls, and the customer watches
            an unchanged screen for several seconds with no sign their
            card was taken. That silence is where people press again or
            give up. These two states were set and never read — this is
            the only place that reads them. */}
        {(sdkState === 'blvd-attached' || sdkState === 'sb-vault-attached') &&
          (submitting || processing) && (
            <div
              class="sbw-vault-progress"
              role="status"
              aria-live="polite"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginTop: '12px',
                fontSize: '13px',
                color: 'var(--sbw-ink-2)',
              }}
            >
              <span class="sbw-vault-progress__dot" aria-hidden="true" />
              Card accepted — confirming your booking…
            </div>
          )}
        {quoteStale && (
          <div class="sbw-error" role="alert">
            We couldn&rsquo;t confirm your updated total. Please refresh the page before paying, so
            you&rsquo;re charged the right amount.
          </div>
        )}
        {formError && (
          <div class="sbw-error" role="alert">
            {formError}
          </div>
        )}
      </div>

      <div class="sbw-actionbar">
        <button
          type="button"
          class="sbw-btn sbw-btn--ghost"
          onClick={onBack}
          disabled={processing || submitting}
          aria-label="Back"
        >
          <BackIcon />
        </button>
        {/* Boulevard's iframe AND the sb-vault iframe carry their own Pay/Save
            button (the form lives inside the iframe and the user submits there).
            The parent only shows submit for the SDK-in-parent providers
            (square-web-payments, stripe). */}
        {deposit.tokenization.kind !== 'blvd-vault' && deposit.tokenization.kind !== 'sb-vault' && (
          <button
            type="submit"
            class="sbw-btn sbw-btn--primary"
            disabled={
              // 'stripe-attached' counts as usable. It is set once the
              // card tokenizes (:845) and NOTHING ever resets it, so
              // excluding it left the Pay button permanently dead after
              // the first failed checkout — the customer stayed on this
              // step with an error banner and no way to act on it, and
              // only a page reload escaped. That was the real "kicked
              // out without confirmation" mechanism on Be You, working
              // alongside the Stripe idempotency collision.
              //
              // Re-enabling is safe: `processing || submitting` already
              // covers the in-flight window, and pressing Pay again
              // re-runs handleStripePay, which mints a fresh
              // payment_method — which is exactly what a retry needs.
              (sdkState !== 'ready' && sdkState !== 'stripe' && sdkState !== 'stripe-attached') ||
              processing ||
              submitting ||
              quoteStale === true
            }
          >
            {processing || submitting
              ? 'Processing…'
              : `Pay ${formatMoney(deposit.amountCents, deposit.currency)} to confirm`}
            <LockIcon />
          </button>
        )}
      </div>
    </form>
  );
}
